import OncolensToast from '@/components/OncolensToast';
import { useOrganizationData } from '@/features/organizations/organization.hooks';
import useToggle from '@/hooks/useToggle';
import { yupResolver } from '@hookform/resolvers/yup';
import Banner from '@oncolens/oncolens-ui/lib/components/Common/Banner/Banner';
import { BannerType } from '@oncolens/oncolens-ui/lib/components/Common/Banner/Banner.helpers';
import Modal from '@oncolens/oncolens-ui/lib/components/Common/Modal';
import Button from '@oncolens/oncolens-ui/lib/components/FormControls/Button';
import ButtonWithLoading from '@oncolens/oncolens-ui/lib/components/FormControls/ButtonWithLoading';
import Checkbox from '@oncolens/oncolens-ui/lib/components/FormControls/Checkbox';
import Combobox from '@oncolens/oncolens-ui/lib/components/FormControls/Combobox/Combobox';
import DefaultButton, {
  DefaultButtonColor,
} from '@oncolens/oncolens-ui/lib/components/FormControls/DefaultButton';
import Listbox from '@oncolens/oncolens-ui/lib/components/FormControls/Listbox';
import TextInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextInput';
import Typeahead, {
  TypeaheadListItem,
} from '@oncolens/oncolens-ui/lib/components/FormControls/Typeahead/Typeahead';
import { ListItem } from '@oncolens/oncolens-ui/lib/components/FormControls/types';
import { CancerConfig } from '@oncolens/oncolens-ui/lib/dto/config/Config';
import useCancerConfig from '@oncolens/oncolens-ui/lib/hooks/config/useCancerConfig';
import FormField from '@oncolens/oncolens-ui/lib/libraries/FormField';
import {
  CreatedFormStore,
  FieldUpdateOptions,
} from '@oncolens/oncolens-ui/lib/libraries/FormStore';
import useFormStore from '@oncolens/oncolens-ui/lib/libraries/useFormStore';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { Controller, ControllerRenderProps, SubmitHandler, useForm } from 'react-hook-form';
import { BsDashCircle } from 'react-icons/bs';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useNavigate } from 'react-router';
import * as yup from 'yup';
import {
  GuidelineTemplateDto,
  useAllGuidelineTemplates,
  useCreateGuidelineTemplate,
  useGuidelineTemplateById,
} from '../api/guideline-template';
import {
  GuideLineType,
  GuidelineConditionDto,
  GuidelineForCreationDto,
  GuidelineForUpdateDto,
  useCreateGuideline,
  useUpdateGuideline,
  useUpdateGuidelineFile,
} from '../api/guidelines';

interface DiseaseCondition {
  cancerTypeId?: string;
  cancerPathologyId?: string;
  cancerLocationId?: string;
  sortOrder: number;
}

export interface GuidelineForManipulation {
  filename?: string | undefined;
  type: typeof GuideLineType | undefined;
  name: string | undefined;
  diseaseConditions: DiseaseCondition[];
  attachmentType?: string;
  url?: string;
  file: FileList | undefined;
  assignAllOrganizations: boolean;
  organizations: {
    label: string;
    value: string;
  }[];
}

const guidelineSchema = yup.object<Record<keyof GuidelineForManipulation, yup.AnySchema>>({
  name: yup.string().required(),
  type: yup.string().required(),
  attachmentType: yup.string().required(),
  url: yup.string().when('attachmentType', {
    is: 'url',
    then: yup.string().url('Must be a valid url').required('A URL is required'),
    otherwise: yup.string().nullable(),
  }),
  file: yup.mixed().when('attachmentType', {
    is: 'file',
    then: yup.mixed().when('filename', {
      is: (filename: string | null | undefined) => !filename || filename.length === 0,
      then: yup.mixed().required('A file is required'),
      otherwise: yup.mixed(),
    }),
    otherwise: yup.mixed(),
  }),
  filename: yup.string().nullable(),
  diseaseConditions: yup.array().of(
    yup.object().shape({
      id: yup.string().nullable(),
      cancerTypeId: yup.string().required(),
      cancerPathologyId: yup.string().nullable(),
      cancerLocationId: yup.string().nullable(),
      sortOrder: yup.number(),
    }),
  ),
  assignAllOrganizations: yup.boolean(),
  organizations: yup
    .array(yup.object().shape({ label: yup.string(), value: yup.string() }))
    .when('assignAllOrganizations', {
      is: (allSelected: boolean) => !allSelected,
      then: (schema) => schema.min(1, 'At least one organization is required.').required(),
    }),
});

const FormMode = ['Add', 'Edit'] as const;
const diseaseConditionsPropName = 'diseaseConditions';

type AddDiseaseConditionProps = {
  cancerTypeId?: string | undefined;
  cancerLocationId?: string | undefined;
  cancerPathologyId?: string | undefined;
  updateField: (path: string, value: any, options?: FieldUpdateOptions | undefined) => void;
  existingDiseaseConditions: DiseaseCondition[];
};
const addDiseaseCondition = ({
  cancerTypeId,
  cancerLocationId,
  cancerPathologyId,
  existingDiseaseConditions,
  updateField,
}: AddDiseaseConditionProps) => {
  updateField(diseaseConditionsPropName, [
    ...existingDiseaseConditions,
    {
      cancerTypeId,
      cancerPathologyId,
      cancerLocationId,
    },
  ]);
};
const clearDiseaseConditions = ({
  updateField,
}: {
  updateField: (path: string, value: any, options?: FieldUpdateOptions | undefined) => void;
}) => {
  updateField(diseaseConditionsPropName, []);
};

export function GuidelineForm({
  guidelineId,
  defaultValues,
}: {
  guidelineId?: string;
  defaultValues: GuidelineForManipulation;
}) {
  const formMode = (guidelineId ? 'Edit' : 'Add') as (typeof FormMode)[number];
  const formStore = useFormStore({ ...defaultValues }, guidelineSchema);
  const [isValid, updateField, getValue, updateStore, reset, attachmentType, url] = formStore(
    (x) => [x.isValid, x.updateField, x.getValue, x.updateStore, x.reset, x.attachmentType, x.url],
  );
  const [attachmentTypeDisabled, setAttachmentTypeDisabled] = useState(false);

  useEffect(() => {
    updateStore((x) => {
      if (defaultValues.url || defaultValues.filename) {
        setAttachmentTypeDisabled(true);
      }
      Object.assign(x, {
        ...defaultValues,
        ...(defaultValues.url && {
          attachmentType: 'url',
          url: defaultValues.url,
        }),
        ...(defaultValues.filename && { attachmentType: 'file' }),
      });
    });
  }, [defaultValues, updateStore]);

  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const guidelineTypes =
    (GuideLineType?.map((type) => ({
      value: type,
      label: type,
    })) as ListItem[]) ?? [];

  const attachmentTypes = [
    {
      label: 'URL',
      value: 'url',
    },
    {
      label: 'File',
      value: 'file',
    },
  ];

  const onSave = ({ leaveToDestination }: { leaveToDestination?: string | null | undefined }) => {
    const store = formStore.getState();

    if (!store.isValid) return;

    setSaving(true);
    const dataToSave = store.getValues();
    if (formMode === 'Add') saveNewGuideline({ dataToSave, leaveToDestination });
    else if (formMode === 'Edit') saveUpdatedGuideline({ dataToSave, leaveToDestination });
  };

  const createGuideline = useCreateGuideline();
  function saveNewGuideline({
    dataToSave,
    leaveToDestination,
  }: {
    dataToSave: GuidelineForManipulation;
    leaveToDestination: string | null | undefined;
  }) {
    const organizationIds =
      dataToSave.organizations.length > 0 ? dataToSave.organizations.map((org) => org.value) : [];

    const formattedData = {
      name: dataToSave.name ?? '',
      type: dataToSave.type ?? '',
      ...(attachmentType === 'url' ? { url } : { file: dataToSave.file }),
      diseaseConditions: dataToSave.diseaseConditions,
      organizations: {
        assignAllOrganizations: dataToSave.assignAllOrganizations,
        organizationIds,
      },
    } as GuidelineForCreationDto;
    createGuideline.mutate(formattedData, {
      onSuccess: () => OncolensToast.success('Guideline created'),
      onError: () => OncolensToast.error('Error creating Guideline'),
      onSettled: () => {
        setSaving(false);
        if (leaveToDestination && (leaveToDestination?.length ?? 0) > 0)
          navigate(leaveToDestination);
      },
    });
  }

  const updateGuideline = useUpdateGuideline(guidelineId);
  function saveUpdatedGuideline({
    dataToSave,
    leaveToDestination,
  }: {
    dataToSave: GuidelineForManipulation;
    leaveToDestination: string | null | undefined;
  }) {
    const organizationIds =
      dataToSave.organizations.length > 0 ? dataToSave.organizations.map((org) => org.value) : [];

    const formattedData = {
      name: dataToSave.name ?? '',
      type: dataToSave.type ?? '',
      ...(dataToSave.url && { url: dataToSave.url }),
      diseaseConditions: dataToSave.diseaseConditions,
      organizations: {
        assignAllOrganizations: dataToSave.assignAllOrganizations,
        organizationIds,
      },
    } as GuidelineForUpdateDto;
    updateGuideline.mutate(formattedData, {
      onSuccess: () => OncolensToast.success('Guideline updated'),
      onError: () => OncolensToast.error('Error updating Guideline'),
      onSettled: () => {
        setSaving(false);
        if (leaveToDestination && (leaveToDestination?.length ?? 0) > 0)
          navigate(leaveToDestination);
      },
    });
  }
  const conditions = getValue(diseaseConditionsPropName);
  const hasValidConditions =
    conditions?.length > 0 && conditions.every((c: { cancerTypeId: string }) => c.cancerTypeId);
  return (
    <div className="max-w-3xl space-y-4 lg:max-w-[88rem]">
      <div className="w-full space-y-4 lg:space-y-0 lg:flex lg:space-x-12">
        <div className="w-full">
          <h2 className="text-xl font-semibold">Guideline Details</h2>
          <div className="grid grid-cols-12 gap-3 mt-2">
            <GuidelineDetailsForm
              formStore={formStore}
              guidelineTypes={guidelineTypes}
              attachmentTypes={attachmentTypes}
              formMode={formMode}
              guidelineId={guidelineId}
              attachmentTypeDisabled={attachmentTypeDisabled}
            />
          </div>
        </div>

        <div className="w-full">
          <h2 className="text-xl font-semibold">Conditions</h2>
          <div className="flex items-end justify-between pt-1 pb-4 border-b border-gray-300">
            <TemplateSelection guidelineFormStore={formStore} />
          </div>

          <div className="flex items-end justify-end pt-2">
            <div className="">
              <Button
                testSelector="add-condition"
                onClick={() => {
                  const values = getValue(diseaseConditionsPropName) as DiseaseCondition[];
                  addDiseaseCondition({
                    cancerTypeId: undefined,
                    cancerPathologyId: undefined,
                    cancerLocationId: undefined,
                    existingDiseaseConditions: values,
                    updateField,
                  });
                }}
                buttonStyle="secondary"
                buttonColor="blue"
                className="w-auto bg-transparent border-none">
                Add Condition
              </Button>
            </div>
          </div>
          <div className="flex-col w-full mt-1 space-y-4">
            <ConditionsForm formStore={formStore} />
          </div>
          <div className="pt-3">
            <SaveAsTemplateModal
              diseaseConditions={conditions}
              hasValidConditions={hasValidConditions}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end pt-3 space-x-3 lg:pt-6 lg:px-2">
        <Button className="w-auto px-4" buttonColor="gray" onClick={() => navigate(-1)}>
          Back
        </Button>

        {formMode === 'Edit' && (
          <ButtonWithLoading
            loading={saving}
            className="w-auto px-6"
            buttonColor="gray"
            onClick={() => onSave({ leaveToDestination: null })}
            disabled={!isValid}>
            Save
          </ButtonWithLoading>
        )}

        <ButtonWithLoading
          loading={saving}
          className="w-auto px-6"
          buttonColor={formMode === 'Add' ? 'gray' : 'blue'}
          onClick={() => onSave({ leaveToDestination: '/behavior-config/guidelines/guideline' })}
          disabled={!isValid}>
          Save & Close
        </ButtonWithLoading>

        {formMode === 'Add' && (
          <ButtonWithLoading
            loading={saving}
            className="w-auto px-6"
            buttonColor="blue"
            onClick={() => {
              onSave({
                leaveToDestination: '/behavior-config/guidelines/guideline/add',
              });
              reset();
            }}
            disabled={!isValid}>
            Save & Add New
          </ButtonWithLoading>
        )}
      </div>
    </div>
  );
}

function GuidelineDetailsForm({
  formStore,
  guidelineTypes,
  attachmentTypes,
  formMode,
  guidelineId,
  attachmentTypeDisabled,
}: {
  formStore: CreatedFormStore<GuidelineForManipulation>;
  guidelineTypes: ListItem[];
  attachmentTypes: ListItem[];
  formMode: (typeof FormMode)[number];
  guidelineId: string | undefined;
  attachmentTypeDisabled: boolean;
}) {
  const orgData = useOrganizationData();
  const [assignAllOrganizations, filename, updateField, attachmentType] = formStore((x) => [
    x.assignAllOrganizations,
    x.filename,
    x.updateField,
    x.attachmentType,
  ]);

  const [fileUpdating, setFileUpdating] = useState(false);
  const updateFile = useUpdateGuidelineFile(guidelineId);
  function updateFileSelection({
    e,
    onChange,
  }: {
    e: React.ChangeEvent<HTMLInputElement>;
    onChange: (file: File) => void;
  }) {
    if (formMode === 'Edit') {
      setFileUpdating(true);
      updateFile.mutate(e.target.files![0], {
        onSuccess: () => {
          OncolensToast.success('File updated');
          updateField('filename', e!.target!.files![0].name);
          onChange(e!.target!.files![0]);
        },
        onError: () => OncolensToast.error('Error updating File'),
        onSettled: () => {
          setFileUpdating(false);
        },
      });
      return;
    }

    onChange(e!.target!.files![0]);
  }

  return (
    <>
      <FormField name="name" store={formStore}>
        {({ value, onChange, errorMessage }) => (
          <div className="col-span-full md:col-span-8">
            <TextInput
              id="name"
              type="text"
              htmlName="name"
              label="Name"
              error={errorMessage}
              required={true}
              value={value}
              onChange={onChange}
              wrapperProps={{ className: 'col-span-6 md:col-span-3' }}
              placeholder="Name"
            />
          </div>
        )}
      </FormField>
      <FormField name="type" store={formStore}>
        {({ value, onChange, errorMessage }) => (
          <div className="col-span-full md:col-span-4">
            <Listbox
              label="Type"
              placeholder="Select a Type"
              data={guidelineTypes}
              selected={value}
              setSelected={(val) => {
                onChange(val);
              }}
              required={true}
              error={errorMessage}
              testSelector="guideline-type"
            />
          </div>
        )}
      </FormField>

      <FormField name="organizations" store={formStore}>
        {({ value, onChange }) => {
          const showBanner = !!value && value.length === orgData.length && !assignAllOrganizations;
          return (
            <div className="col-span-full">
              <Combobox
                id="organizations"
                htmlName="organizations"
                label="Organizations"
                data={orgData}
                value={value}
                // eslint-disable-next-line @typescript-eslint/no-shadow
                onChange={(value) => onChange(value)}
                required={!assignAllOrganizations}
                disabled={assignAllOrganizations}
                useAutocomplete
                useMultipleSelect
              />
              {showBanner && (
                <Banner type={BannerType.Warning} className="max-w-md mt-2 text-xs">
                  Any future organizations will not be included when selecting this function. To
                  ensure future organizations are included, select the checkbox under this message
                  titled &apos;Select all organizations&apos;.
                </Banner>
              )}
            </div>
          );
        }}
      </FormField>

      <FormField name="assignAllOrganizations" store={formStore}>
        {({ onChange, value }) => (
          <Checkbox
            id="assignAllOrganizations"
            htmlName="assignAllOrganizations"
            label="Select All Organizations"
            onClick={(e) => onChange(e.target.checked)}
            value={value}
            wrapperProps={{ className: 'col-span-full' }}
          />
        )}
      </FormField>

      <FormField name="attachmentType" store={formStore}>
        {({ value, onChange, errorMessage }) => (
          <div className="col-span-full">
            <Listbox
              label="Attachment Type"
              placeholder="Select whether URL or file"
              data={attachmentTypes}
              selected={value}
              setSelected={(val) => {
                onChange(val);
              }}
              disabled={attachmentTypeDisabled}
              required={true}
              error={errorMessage}
              testSelector="attachment-type"
            />
          </div>
        )}
      </FormField>

      {attachmentType === 'url' && (
        <FormField name="url" store={formStore}>
          {({ value, onChange, errorMessage }) => (
            <div className="col-span-full">
              <TextInput
                id="url"
                type="text"
                htmlName="url"
                label="URL"
                error={errorMessage}
                required={true}
                value={value}
                onChange={onChange}
                wrapperProps={{ className: 'col-span-6 md:col-span-3' }}
                placeholder="URL"
              />
            </div>
          )}
        </FormField>
      )}

      {attachmentType === 'file' && (
        <FormField name="file" store={formStore}>
          {({ value, onChange }) => (
            <div className="col-span-full md:col-span-10">
              <label
                htmlFor="file-upload"
                className="flex items-center text-sm font-medium text-gray-700"
                data-public="true">
                Guideline File
                <span className="ml-1 text-red-500 not-sr-only">*</span>
              </label>
              <div
                className={clsx(
                  'block w-full sm:text-sm rounded-md',
                  'py-1.5 pl-1 pr-3',
                  'bg-white text-gray-500',
                  'group',
                  'border border-oncolensOrange-500 shadow-sm',
                )}>
                <label
                  className="sm:flex sm:items-center sm:justify-between"
                  data-cy="fileupload"
                  htmlFor="file-upload">
                  <span className="ml-2 text-sm select-none">
                    {(value?.name ?? filename) || 'No file selected'}
                  </span>
                  <input
                    type="file"
                    name={'file-upload'}
                    id={'file-upload'}
                    placeholder={'file-upload'}
                    onChange={(e) => updateFileSelection({ e, onChange })}
                    className="hidden"
                  />
                  <div
                    className={clsx(
                      'shadow-sm text-sm font-medium py-0.5 px-1.5 rounded text-center bg-white group-hover:bg-gray-200 group-hover:text-gray-800 text-gray-700 border border-gray-200 hover:border-gray-300 transition-colors select-none',
                      fileUpdating && 'cursor-not-allowed animate-pulse',
                    )}>
                    Select a File
                  </div>
                </label>
              </div>
            </div>
          )}
        </FormField>
      )}
    </>
  );
}

function ConditionsForm({ formStore }: { formStore: CreatedFormStore<GuidelineForManipulation> }) {
  const { data: cancerConfigs } = useCancerConfig();
  return (
    <FormField name="diseaseConditions" store={formStore}>
      {({ value, onChange }) =>
        (value as GuidelineConditionDto[]).map((item, index) => {
          const conditionsValue = value as GuidelineConditionDto[];
          return (
            <div
              key={`guideline-condition-item-${index}`}
              data-cy={`guideline-condition-item-${index}`}
              className="flex items-end w-full space-x-4">
              <GuidelineConditionItem
                value={item}
                onChange={(newValue) =>
                  onChange(
                    // eslint-disable-next-line @typescript-eslint/no-shadow
                    conditionsValue.map((item, itemIndex) =>
                      itemIndex === index
                        ? {
                            ...newValue,
                            sortOrder: itemIndex,
                          }
                        : { ...item, sortOrder: itemIndex },
                    ),
                  )
                }
                cancerConfigs={cancerConfigs ?? []}
                index={index}
              />
              <div className="mb-0.5 flex-0">
                <Button
                  testSelector="delete-condition"
                  disabled={conditionsValue.length < 2}
                  onClick={() => {
                    onChange(conditionsValue.filter((_, i) => i !== index));
                  }}
                  buttonStyle="secondary"
                  className={clsx(
                    'border-none',
                    !(conditionsValue.length < 2) && 'hover:bg-gray-100',
                  )}>
                  <BsDashCircle className="w-4 h-4" />
                </Button>
              </div>
            </div>
          );
        })
      }
    </FormField>
  );
}

function GuidelineConditionItem({
  value,
  onChange,
  cancerConfigs,
  index,
}: {
  value: any;
  onChange: (newValue: GuidelineConditionDto) => void;
  cancerConfigs: CancerConfig[];
  index: number;
}) {
  const selectedCancerIdItem = cancerConfigs.find(
    (cancerConfig) => cancerConfig.value === value.cancerTypeId,
  );
  const locationOptions: ListItem[] = selectedCancerIdItem?.locations || [];

  const selectedLocationItem = locationOptions.find(
    (locationConfig) => locationConfig.value === value.cancerLocationId,
  );
  const typeOptions: ListItem[] = selectedCancerIdItem?.pathologies || [];

  const selectedTypeItem = typeOptions.find(
    (locationConfig) => locationConfig.value === value.cancerPathologyId,
  );

  return (
    <div className="flex flex-1 space-x-4">
      <Combobox
        id="guideline-condition-cancer-id"
        htmlName="guideline-condition-cancer-id"
        wrapperProps={{
          className: 'w-full',
        }}
        label="Cancer"
        useSrOnlyLabel={index > 0}
        data={cancerConfigs}
        value={selectedCancerIdItem as ListItem}
        onChange={(selectedValue) => {
          onChange({
            ...value,
            cancerTypeId: selectedValue ? (selectedValue as ListItem).value : undefined,
            cancerLocationId: undefined,
            cancerPathologyId: undefined,
          });
        }}
        required
        useMultipleSelect={false}
        testSelector="combobox-cancer"
        useAutocomplete
        placeholder="Select a cancer"
      />
      <Combobox
        id="guideline-condition-location"
        htmlName="guideline-condition-location"
        wrapperProps={{
          className: 'w-full',
        }}
        label="Location"
        useSrOnlyLabel={index > 0}
        data={locationOptions}
        value={selectedLocationItem as ListItem}
        onChange={(selectedValue) => {
          onChange({
            ...value,
            cancerLocationId: selectedValue ? (selectedValue as ListItem).value : undefined,
          });
        }}
        useMultipleSelect={false}
        disabled={(selectedCancerIdItem?.value.length ?? 0) <= 0}
        testSelector="combobox-location"
        useAutocomplete
        placeholder="Select a location"
      />
      <Combobox
        id="guideline-condition-type"
        htmlName="guideline-condition-type"
        wrapperProps={{
          className: 'w-full',
        }}
        label="Type"
        useSrOnlyLabel={index > 0}
        data={typeOptions}
        value={selectedTypeItem as ListItem}
        onChange={(selectedValue) => {
          onChange({
            ...value,
            cancerPathologyId: selectedValue ? (selectedValue as ListItem).value : undefined,
          });
        }}
        useMultipleSelect={false}
        disabled={(selectedCancerIdItem?.value.length ?? 0) <= 0}
        testSelector="combobox-type"
        useAutocomplete
        placeholder="Select a type"
      />
    </div>
  );
}

function TemplateSelection({
  guidelineFormStore,
}: {
  guidelineFormStore: CreatedFormStore<GuidelineForManipulation>;
}) {
  const { control, watch, handleSubmit, reset } = useForm<FormValues>();
  const [updateField, getValue] = guidelineFormStore((x) => [x.updateField, x.getValue]);

  type FormValues = {
    templateToApply: string;
  };

  const { data: templates } = useAllGuidelineTemplates({}) ?? { data: [] };
  const templateItems = templates?.map((template: GuidelineTemplateDto) => ({
    label: template.name,
    value: template.id,
  }));

  const onSubmit: SubmitHandler<FormValues> = () => {
    clearDiseaseConditions({ updateField });

    // eslint-disable-next-line array-callback-return
    templateToApplyData?.diseaseConditions?.map((condition) => {
      const values = getValue(diseaseConditionsPropName) as DiseaseCondition[];
      addDiseaseCondition({
        cancerTypeId: condition?.cancerTypeId,
        cancerPathologyId: condition?.cancerPathologyId,
        cancerLocationId: condition?.cancerLocationId,
        existingDiseaseConditions: values,
        updateField,
      });
    });

    reset();
  };
  const templateToApply = watch('templateToApply');
  const canApplyTemplate = (templateToApply?.length ?? 0) > 0;
  const { data: templateToApplyData } = useGuidelineTemplateById(templateToApply);

  const handleTemplateChange = ({
    item,
    field,
  }: {
    item: TypeaheadListItem | null;
    field: ControllerRenderProps<FormValues, 'templateToApply'>;
  }) => {
    field.onChange(item ? item.value : '');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex items-end flex-1 space-x-2">
      <Controller
        name="templateToApply"
        control={control}
        render={({ field }) => {
          const selectedItem =
            templateItems?.find((template) => template.value === field.value) || null;
          return (
            <Typeahead
              id="template-to-apply"
              label="Template Name"
              data={templateItems ?? []}
              className="flex-1"
              selected={selectedItem}
              placeholder="Start typing a template name..."
              options={{ clearable: true }}
              sortingOptions={{ sortable: true, sortProperty: 'label' }}
              onChange={(item) => {
                handleTemplateChange({ item, field });
              }}
            />
          );
        }}
      />

      <Button
        testSelector="add-condition"
        type="submit"
        buttonStyle="secondary"
        buttonColor="orange"
        className="w-auto"
        disabled={!canApplyTemplate}>
        Apply Template
      </Button>
    </form>
  );
}

type NewTemplateCreation = {
  templateName: string;
};

const templateSchema = yup.object().shape({
  templateName: yup.string().required('Template name is required'),
});

function SaveAsTemplateModal({
  diseaseConditions,
  hasValidConditions,
}: {
  diseaseConditions: DiseaseCondition[];
  hasValidConditions: boolean;
}) {
  const { data: cancerConfigs } = useCancerConfig();
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    reset,
  } = useForm<NewTemplateCreation>({
    resolver: yupResolver(templateSchema),
    mode: 'onChange',
    defaultValues: {
      templateName: '',
    },
  });
  const templateName = watch('templateName');
  const createGuidelineTemplate = useCreateGuidelineTemplate();
  const onSubmit: SubmitHandler<NewTemplateCreation> = () => {
    const formattedData = {
      name: templateName,
      diseaseConditions: diseaseConditions.map((condition) => ({
        cancerTypeId: condition.cancerTypeId,
        cancerPathologyId: condition.cancerPathologyId,
        cancerLocationId: condition.cancerLocationId,
        sortOrder: condition.sortOrder,
      })),
    };
    createGuidelineTemplate.mutate(formattedData, {
      onSuccess: () => OncolensToast.success('Template added'),
      onError: () => OncolensToast.error('Error creating Template'),
      onSettled: () => {
        reset();
        toggleModal();
      },
    });
  };

  const [showModal, toggleModal] = useToggle(false);

  return (
    <>
      <Button
        testSelector="save-as-template"
        buttonStyle="secondary"
        buttonColor="blue"
        className="w-auto bg-transparent border-none"
        disabled={!hasValidConditions}
        onClick={() => toggleModal()}>
        Save as Template
      </Button>
      <Modal show={showModal} toggleOverride={toggleModal} testSelector={'new-template-modal'}>
        <Modal.Header title="Save as Template" showClose />
        <Modal.Content>
          <p>Do you want to save these disease conditions as a template?</p>
          <div className="pt-3">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="templateName"
                control={control}
                render={({ field }) => (
                  <TextInput
                    id="templateName"
                    label="Template Name"
                    htmlName="templateName"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.templateName?.message}
                    required
                    type="text"
                    testSelector="new-template-name"
                    wrapperProps={{ className: 'w-2/3' }}
                  />
                )}
              />
              <div className="hidden sm:items-end sm:flex-1 sm:pt-2 sm:pl-6 sm:space-x-2 sm:flex">
                <ul className="pt-4 space-y-2 overflow-y-auto text-sm max-h-64 md:max-h-96">
                  {diseaseConditions.map((condition, index) => {
                    const definedCancerConfigs = cancerConfigs ?? [];
                    const selectedCancerIdItem = definedCancerConfigs.find(
                      (cancerConfig) => cancerConfig.value === condition.cancerTypeId,
                    );
                    const locationOptions: ListItem[] = selectedCancerIdItem?.locations || [];

                    const selectedLocationItem = locationOptions.find(
                      (locationConfig) => locationConfig.value === condition.cancerLocationId,
                    );
                    const typeOptions: ListItem[] = selectedCancerIdItem?.pathologies || [];

                    const selectedTypeItem = typeOptions.find(
                      (locationConfig) => locationConfig.value === condition.cancerPathologyId,
                    );

                    let topographyLabel = `${selectedCancerIdItem?.label}`;
                    if (selectedLocationItem?.label) {
                      topographyLabel = `${topographyLabel} - ${selectedLocationItem?.label}`;
                    }

                    return (
                      <li key={index} className="pl-4 border-l-2 border-blue-500">
                        <p className="font-medium text-gray-800">{topographyLabel}</p>
                        <p className="text-sm text-gray-500">{selectedTypeItem?.label}</p>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <Modal.Buttons className="pt-4 sm:pt-2 flex space-x-2.5 items-center">
                {(toggle) => (
                  <>
                    <DefaultButton
                      className="ml-auto"
                      onClick={() => {
                        reset();
                        toggle();
                      }}
                      color={DefaultButtonColor.Neutral}>
                      Cancel
                    </DefaultButton>
                    <DefaultButton
                      type="submit"
                      color={DefaultButtonColor.Orange}
                      testSelector={'submit-new-template'}
                      disabled={!isValid}>
                      Save
                    </DefaultButton>
                  </>
                )}
              </Modal.Buttons>
            </form>
          </div>
        </Modal.Content>
      </Modal>
    </>
  );
}
